import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core'
import { Router } from '@angular/router'
import { Subject } from 'rxjs'
import { first, takeUntil } from 'rxjs/operators'
import { AccountService } from 'src/app/account/services/account.service'
import { ApplicationCreatorState, TemplateChoice } from 'src/app/account/services/application-creator-state.service'
import { AccessScope, AccessService } from 'src/app/services/access.service'
import { ApiService } from 'src/app/services/api.service'
import { ApplicationType, SyncPairAccessDto, SyncPairInfo } from 'src/app/services/dtos'
import { RegionsService } from 'src/app/services/regions.service'
import { UiService } from 'src/app/services/ui.service'
import { handleArpioError } from 'src/utils/handle-error'
import { stackSetsModeActive, templatesOutOfDateOnly } from '../../services/outdated-templates'
import { SyncPairAccessCache } from '../../services/sync-pair-access-cache.service'

export type TemplateChoiceOption = NonNullable<TemplateChoice>

@Component({
  // tslint:disable-next-line:component-selector
  selector: 'application-creator-template-choice-page',
  templateUrl: './application-creator-template-choice-page.component.html',
  styleUrls: ['./application-creator-template-choice-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ApplicationCreatorTemplateChoicePageComponent implements OnInit, OnDestroy {
  selectedChoice: TemplateChoiceOption = 'update'
  loading = true
  loadFailed = false

  private destroy$ = new Subject<void>()
  private sourceAccess: AccessService = null
  private targetAccess: AccessService = null

  constructor(
    private applicationCreatorState: ApplicationCreatorState,
    private apiService: ApiService,
    public uiService: UiService,
    private accountService: AccountService,
    private router: Router,
    private regionsService: RegionsService,
    private accessCache: SyncPairAccessCache
  ) {}

  ngOnInit() {
    this.accountService.accountIdStream.pipe(first(), takeUntil(this.destroy$)).subscribe((loadedAccountId) => {
      this.initializeAfterAccountLoad(loadedAccountId)
    })
  }

  ngOnDestroy(): void {
    this.destroy$.next()
    this.destroy$.complete()
  }

  retryAccessCheck(): void {
    this.loadFailed = false
    this.loading = true
    this.accessCache.clear()
    this.initializeAfterAccountLoad(this.accountId)
  }

  private initializeAfterAccountLoad(loadedAccountId: string): void {
    if (!this.applicationCreatorState.validAccountsAndRegions(this.regionsService)) {
      this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(loadedAccountId), { replaceUrl: true })
      return
    }

    // The whole step is behind the capability. Without it the connect page's hard
    // block is the only behavior, so there is nothing to choose.
    if (!this.accountService.entitledForCapability('enableOutdatedTemplatesReuse')) {
      this.redirectToConnectPage()
      return
    }

    const requestDto = this.syncPair
    const cached = this.accessCache.get(requestDto)
    if (cached) {
      this.applyAccessResult(requestDto, cached)
      return
    }

    // Reached on a refresh, a direct link, or /test_page, where nobody ran the check
    // for us. The connect page reads the same cache, so this still runs only once.
    this.apiService
      .testSyncPairAccess(requestDto)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (accessResult) => {
          this.accessCache.put(requestDto, accessResult)
          this.applyAccessResult(requestDto, accessResult)
        },
        (error) => {
          // The spinner is the whole page, so leaving `loading` set would strand the
          // customer with no BACK and no retry.
          this.loadFailed = true
          this.loading = false
          handleArpioError(window.location.href, this.apiService, this.uiService)(error)
        }
      )
  }

  private applyAccessResult(requestDto: SyncPairInfo, accessResult: SyncPairAccessDto): void {
    this.sourceAccess = new AccessService(this.apiService, this.uiService, requestDto, AccessScope.SOURCE, accessResult)
    this.targetAccess = new AccessService(this.apiService, this.uiService, requestDto, AccessScope.TARGET, accessResult)

    if (!templatesOutOfDateOnly(this.sourceAccess, this.targetAccess, this.stackSetsMode)) {
      this.redirectToConnectPage()
      return
    }

    this.selectedChoice = this.applicationCreatorState.templateChoice ?? 'update'
    this.loading = false
  }

  private get syncPair(): SyncPairInfo {
    return {
      accountId: this.accountService.accountId,
      sourceEndpointUri: this.applicationCreatorState.sourceEndpointUri,
      targetEndpointUri: this.applicationCreatorState.targetEndpointUri,
      identityProviderId: this.applicationCreatorState.identityProviderId,
    }
  }

  private get stackSetsMode(): boolean {
    return stackSetsModeActive(
      this.applicationCreatorState.applicationType,
      this.applicationCreatorState.accessMethod,
      this.accountService
    )
  }

  get accountId(): string {
    return this.accountService.accountId
  }

  get isAzure(): boolean {
    return this.applicationCreatorState.applicationType === ApplicationType.AZURE
  }

  get loadingText(): string {
    if (this.applicationCreatorState.applicationType === ApplicationType.AWS) {
      return 'Checking for AWS connectivity'
    } else if (this.isAzure) {
      return 'Checking for Azure connectivity'
    }
    return 'Validating access...'
  }

  private get sourceTemplateBehind(): boolean {
    return !(this.sourceAccess?.hasLatestVersion ?? true)
  }

  private get targetTemplateBehind(): boolean {
    return !(this.targetAccess?.hasLatestVersion ?? true)
  }

  // Which resource types Arpio can discover and protect is gated on the source
  // template, so a behind recovery alone does not narrow protection scope -- it only
  // limits what some types need to recover. Copy has to say so.
  get recoveryOnlyBehind(): boolean {
    return this.targetTemplateBehind && !this.sourceTemplateBehind
  }

  selectChoice(choice: TemplateChoiceOption): void {
    this.selectedChoice = choice
  }

  backButtonClick(): void {
    this.router.navigateByUrl(this.uiService.buildNewApplicationUrl(this.accountId))
  }

  continueButtonClick(): void {
    this.applicationCreatorState.templateChoice = this.selectedChoice
    this.applicationCreatorState.saveToLocalStorage()

    if (this.selectedChoice === 'update') {
      this.router.navigateByUrl(this.uiService.buildNewApplicationConnectionUrl(this.accountId))
      return
    }
    this.router.navigateByUrl(this.uiService.buildNewApplicationResourcesUrl(this.accountId))
  }

  // Replaces this step in history: a pass-through has nothing to come back to, and
  // BACK landing here again would just redirect forward.
  private redirectToConnectPage(): void {
    this.router.navigateByUrl(this.uiService.buildNewApplicationConnectionUrl(this.accountId), { replaceUrl: true })
  }
}
